/**
 * ClaimSuccess — Iter V2 Phase B (updated in Phase C2).
 *
 * Post-claim celebratory landing. Reached after Path A (claim code) success.
 * Shows a compact certificate card, one-time gold/white confetti,
 * and clear next-step CTAs. Respects `prefers-reduced-motion` and
 * fires confetti at most once per navigation (guarded by a `useRef`).
 *
 * Data policy: renders ONLY public certificate fields. Never surfaces
 * claim_code, private notes, or email addresses.
 *
 * C2 safety: before rendering the celebration we verify from the public
 * registry payload that the certificate is actually `owned` AND owned by
 * the currently authenticated collector. Anyone deep-linking to
 * `/dashboard/claim/success?cert=<SOMEONE_ELSES_CERT>` is bounced.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Trophy, ArrowRight, CheckCircle2, User2, LayoutDashboard } from "lucide-react";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { useCollectorAuth } from "@/context/CollectorAuthContext";

const CERT_RE = /^[A-Z]{2}-\d{4}-\d{4}$/;

export default function ClaimSuccess() {
  const { collector } = useCollectorAuth();
  const [params] = useSearchParams();
  const cert = (params.get("cert") || "").trim().toUpperCase();
  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const firedRef = useRef(false);

  // -------- load public certificate details --------
  useEffect(() => {
    if (!CERT_RE.test(cert)) { setNotFound(true); return; }
    let cancelled = false;
    api.get(`/public/registry/certificates/${cert}`)
      .then((r) => { if (!cancelled) setDetail(r.data); })
      .catch(() => { if (!cancelled) setNotFound(true); });
    return () => { cancelled = true; };
  }, [cert]);

  // -------- C2 ownership guard (synchronous, derived) --------
  // Only celebrate when the certificate is genuinely claimed AND
  // owned by the currently authenticated collector. The public
  // registry endpoint exposes ownership as top-level `is_claimed`
  // + `owner_public_collector_id`. Anything else (unclaimed, owned
  // by another collector, missing viewer identity) renders the
  // bounce below.
  const ownershipOk = !!(
    detail &&
    detail.is_claimed === true &&
    detail.owner_public_collector_id &&
    collector?.public_collector_id &&
    detail.owner_public_collector_id === collector.public_collector_id
  );

  // -------- confetti (one-shot, reduced-motion aware) --------
  useEffect(() => {
    if (firedRef.current) return;
    if (!detail) return;
    // C2 — never fire the celebration effect on a bounce.
    if (!ownershipOk) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { firedRef.current = true; return; }
    firedRef.current = true;
    const gold = "#C8A24D";
    const goldSoft = "#E4C989";
    const white = "#FFFFFF";
    // Two bursts, small, quick, no sound. Total ≈ 900ms of animation life.
    const burst = (originX) => confetti({
      particleCount: 55,
      startVelocity: 32,
      spread: 55,
      ticks: 130,
      gravity: 1.05,
      scalar: 0.9,
      origin: { x: originX, y: 0.35 },
      colors: [gold, goldSoft, white],
      disableForReducedMotion: true,
    });
    burst(0.32);
    setTimeout(() => burst(0.68), 160);
    return () => confetti.reset?.();
  }, [detail]);

  if (!CERT_RE.test(cert)) {
    // Malformed cert — drop it entirely to avoid propagating garbage
    // through the follow-up URL.
    return <Navigate to="/claim" replace />;
  }

  if (notFound) {
    // Valid-shape cert that we can't verify (private / unpublished /
    // registry 404). We still preserve the printed number so the user
    // can proceed to the correct claim entry.
    return <Navigate to={`/claim?cert=${encodeURIComponent(cert)}`} replace />;
  }

  if (!detail) {
    return (
      <div
        className="min-h-[70vh] grid place-items-center bg-[var(--ink)] text-white/70"
        data-testid="claim-success-loading"
      >
        Loading your certificate…
      </div>
    );
  }

  // C2 — false-success guard: if the certificate is not actually
  // claimed by the current collector, we NEVER render a celebration.
  // Sending them to the ClaimCodeForm (with the cert prefilled) lets
  // legitimate owners recover from a stale URL and everyone else
  // simply lands on the generic claim entry — no info leak.
  if (!ownershipOk) {
    return <Navigate to={`/claim?cert=${encodeURIComponent(cert)}`} replace />;
  }

  const publicCollectorId = collector?.public_collector_id;
  const firstName = ((collector?.first_name || collector?.display_name || "").trim().split(" ")[0]) || "Collector";
  const albumLine = [detail.album_label, detail.tournament_year].filter(Boolean).join(" · ");
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const certImageUrl = `${backend}/api/public/certificates/${detail.cert_number}/og.png`;

  // C2 — canonical public profile route. Prefer the collector's
  // chosen username (the URL every share card already points at); if
  // that's somehow missing we fall through to the private dashboard
  // rather than trying to guess a slug.
  const profileHref = collector?.username
    ? `/collectors/${collector.username}`
    : "/dashboard";

  return (
    <div
      className="relative min-h-[calc(100vh-140px)] bg-[var(--ink)] text-white overflow-hidden"
      data-testid="claim-success-page"
    >
      <SEO title={`Certificate ${detail.cert_number} · Claimed`} />

      {/* Ambient decorative glows */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full opacity-[0.35] blur-3xl"
             style={{ background: "radial-gradient(closest-side, rgba(200,162,77,0.6), transparent 70%)" }} />
        <div className="absolute inset-0 opacity-[0.06]"
             style={{
               backgroundImage:
                 "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)," +
                 "linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
               backgroundSize: "48px 48px",
             }} />
      </div>

      <div className="relative container-gm py-16 md:py-24 max-w-3xl mx-auto">

        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[var(--collector-gold)]/40 bg-[var(--collector-gold)]/10 text-[var(--collector-gold-soft)] text-xs font-bold tracking-widest uppercase"
          data-testid="claim-success-badge"
        >
          <CheckCircle2 size={14} /> Ownership Verified
        </div>

        <h1
          className="mt-6 font-serif text-4xl md:text-6xl leading-tight"
          data-testid="claim-success-headline"
        >
          Congratulations, <span className="text-[var(--collector-gold)]">{firstName}</span>!
        </h1>

        <p className="mt-5 max-w-xl text-white/85 text-base md:text-lg leading-relaxed" data-testid="claim-success-live">
          Your certificate <span className="font-mono font-bold text-white">{detail.cert_number}</span> is officially <strong>live</strong> and permanently linked to your account.
        </p>

        {/* Prominent certificate image — the actual OG render. */}
        <div className="mt-8 rounded-3xl overflow-hidden border border-[var(--collector-gold)]/40 bg-white/[0.02]" data-testid="claim-success-cert-image-wrap">
          <img
            src={certImageUrl}
            alt={`Certificate ${detail.cert_number}`}
            data-testid="claim-success-cert-image"
            loading="eager"
            className="w-full h-auto block"
          />
        </div>

        {/* Cert card */}
        <div
          className="mt-10 rounded-3xl border border-[var(--collector-gold)]/30 bg-white/[0.03] backdrop-blur-sm p-6 md:p-8"
          data-testid="claim-success-cert-card"
        >
          <div className="grid md:grid-cols-[auto_1fr] gap-6 items-center">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-[var(--collector-gold)]/15 border border-[var(--collector-gold)]/40 grid place-items-center">
              <Trophy className="text-[var(--collector-gold)]" size={34} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.24em] font-bold text-[var(--collector-gold-soft)] uppercase">
                Album Completion Certificate
              </div>
              <div className="mt-1 font-serif text-2xl md:text-3xl truncate">
                {albumLine || "Album completion certificate"}
              </div>
              <div className="mt-2 text-xs text-white/60 font-mono">
                {detail.cert_number} · {detail.album_type}
              </div>
              {publicCollectorId && (
                <div className="mt-4 inline-flex items-center gap-2 text-xs font-mono text-white/75">
                  <User2 size={12} className="text-[var(--collector-gold-soft)]" />
                  Registered to <span className="font-bold text-white">{publicCollectorId}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions — C2: three prominent buttons in preferred order.
            (1) View My Certificate  → the just-claimed cert public page
            (2) View My Collector Profile  → canonical `/collectors/:slug`
            (3) Go to Dashboard  → collector dashboard.
            All three sit above the fold on mobile with ≥ 44px targets. */}
        <div className="mt-8 space-y-3">
          <Link
            to={`/certificates/${detail.cert_number}`}
            data-testid="claim-success-view-cert"
            className="group w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[var(--collector-gold)] hover:bg-[var(--collector-gold-soft)] text-[var(--ink)] font-display uppercase tracking-wide text-sm md:text-base px-6 py-4 transition-colors"
          >
            <Trophy size={18} /> View My Certificate <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to={profileHref}
            data-testid="claim-success-view-profile"
            className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-[var(--collector-gold)]/60 hover:border-[var(--collector-gold)] hover:bg-white/[0.04] text-white font-display uppercase tracking-wide text-sm md:text-base px-6 py-4 transition-colors"
          >
            <User2 size={18} /> View My Collector Profile
          </Link>
          <Link
            to="/dashboard"
            data-testid="claim-success-view-dashboard"
            className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 hover:border-white/40 hover:bg-white/[0.03] text-white/85 font-bold uppercase tracking-wide text-xs md:text-sm px-6 py-3.5 transition-colors"
          >
            <LayoutDashboard size={16} /> Go to Dashboard
          </Link>
        </div>

        {/* Next steps */}
        <div className="mt-14 border-t border-white/10 pt-8">
          <div className="text-[10px] tracking-[0.24em] font-bold text-[var(--collector-gold-soft)] uppercase">
            What next
          </div>
          <ul className="mt-4 space-y-3 text-white/80 text-sm md:text-base">
            <li className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--collector-gold)] shrink-0" />
              Visit your <Link to="/dashboard" className="text-[var(--collector-gold-soft)] underline underline-offset-4 hover:text-white">collector dashboard</Link> to see every claimed certificate in one place.
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--collector-gold)] shrink-0" />
              Have more certificates? Head back to <Link to="/claim" className="text-[var(--collector-gold-soft)] underline underline-offset-4 hover:text-white">Claim</Link> and enter the next code.
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[var(--collector-gold)] shrink-0" />
              Turn on a public profile in your dashboard to make your trophy case discoverable.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
